import React, { useEffect, useState, useMemo } from "react";
import HeaderPrint from "../../../Pdf/PrintHeaderAccount";
import axios from "axios";
import config from "../../../config";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import PrintFooter from "../../../Pdf/PrintFooter";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import moment from "moment";
import { Spin } from "antd";

const COL_ALL = {
  sr: 26,
  product: 78,
  dated: 44,
  opStock: 50,
  opValue: 55,
  purchase: 50,
  purchaseVal: 55,
  sales: 50,
  salesVal: 55,
  closingStock: 50,
  closingVal: 54,
};

const COL_SINGLE = {
  sr: 26,
  dated: 60,
  opStock: 60,
  opValue: 60,
  purchase: 60,
  purchaseVal: 60,
  sales: 60,
  salesVal: 60,
  closingStock: 60,
  closingVal: 61,
};

const fmtAmt = (val) => PrintFormateNumber(parseReportAmount(val), 0);
const fmtQty = (val) => PrintFormateNumber(parseReportAmount(val));

const ThCell = ({ width, label, align }) => (
  <View style={[saleReportStyles.th, { width, textAlign: align || "left" }]}>
    <Text style={saleReportStyles.thValue}>{label}</Text>
  </View>
);

const TdCell = ({ width, value, align }) => (
  <View style={[saleReportStyles.td, { width, textAlign: align || "left" }]}>
    <Text style={saleReportStyles.tdValue}>{value ?? "—"}</Text>
  </View>
);

const PrintProductWiseSummary = () => {
  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");
  const urlParams = new URLSearchParams(window.location.search);

  const StartDate = urlParams.get("StartDate");
  const EndDate = urlParams.get("EndDate");
  const ProductID = urlParams.get("ProductID");
  const hideEmptyDays = urlParams.get("HideZeroBalance") !== "0";

  const [productList, setProductList] = useState([]);
  const [reportData, setReportData] = useState({});
  const [loading, setLoading] = useState(false);

  const showProductColumn = !ProductID || ProductID === "0";
  const COL = showProductColumn ? COL_ALL : COL_SINGLE;
  const TABLE_WIDTH = useMemo(
    () => Object.values(COL).reduce((sum, w) => sum + w, 0),
    [COL]
  );

  const formattedStart = StartDate
    ? moment(StartDate).isValid()
      ? moment(StartDate).format("YYYY-MM-DD")
      : StartDate
    : null;
  const formattedEnd = EndDate
    ? moment(EndDate).isValid()
      ? moment(EndDate).format("YYYY-MM-DD")
      : EndDate
    : null;

  useEffect(() => {
    document.title = "Print Productwise Summary";
    if (!formattedStart || !formattedEnd || !UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Report/GetDateWiseProductSummary",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        PageNo: 1,
        PageSize: 10000,
        StartDate: formattedStart,
        EndDate: formattedEnd,
        ProductID: ProductID ? Number(ProductID) : 0,
        HideZeroBalance: hideEmptyDays,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setProductList(response.data.ListofSummary || []);
          setReportData(response.data);
        } else {
          setProductList([]);
          setReportData({});
        }
        setLoading(false);
      })
      .catch(function () {
        setProductList([]);
        setReportData({});
        setLoading(false);
      });
  }, [formattedStart, formattedEnd, ProductID, hideEmptyDays, UserID, AccessKey]);

  const periodLabel = `${
    formattedStart && moment(formattedStart).isValid()
      ? moment(formattedStart).format(config.date_format)
      : "—"
  } to ${
    formattedEnd && moment(formattedEnd).isValid()
      ? moment(formattedEnd).format(config.date_format)
      : "—"
  }`;

  const productLabel = showProductColumn
    ? "All products"
    : productList[0]?.ProductName || `Product #${ProductID}`;

  const labelWidth =
    COL.sr +
    (COL.product || 0) +
    COL.dated +
    COL.opStock +
    COL.opValue;

  const generateData = () =>
    productList.map((item, index) => (
      <View key={index} style={[saleReportStyles.row, { width: TABLE_WIDTH }]}>
        <TdCell width={COL.sr} value={String(index + 1)} />
        {showProductColumn && (
          <TdCell width={COL.product} value={item.ProductName} />
        )}
        <TdCell
          width={COL.dated}
          value={
            item.Dated ? moment(item.Dated).format(config.date_format) : "—"
          }
        />
        <TdCell width={COL.opStock} value={fmtQty(item.OpeningStock)} align="right" />
        <TdCell
          width={COL.opValue}
          value={fmtAmt(item.OpeningStockValue)}
          align="right"
        />
        <TdCell width={COL.purchase} value={fmtQty(item.Purchase)} align="right" />
        <TdCell
          width={COL.purchaseVal}
          value={fmtAmt(item.PurchaseValue)}
          align="right"
        />
        <TdCell width={COL.sales} value={fmtQty(item.Sales)} align="right" />
        <TdCell
          width={COL.salesVal}
          value={fmtAmt(item.SalesValue)}
          align="right"
        />
        <TdCell
          width={COL.closingStock}
          value={fmtQty(item.ClosingStock)}
          align="right"
        />
        <TdCell
          width={COL.closingVal}
          value={fmtAmt(item.ClosingStockValue)}
          align="right"
        />
      </View>
    ));

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page} wrap>
        <HeaderPrint compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>
              Datewise Product Summary
            </Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Period: {periodLabel}
            </Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Product: {productLabel}
            </Text>
            {hideEmptyDays && (
              <Text style={saleReportStyles.reportSubtitle}>
                Showing days with stock movement or activity only
              </Text>
            )}
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            <View style={[saleReportStyles.tableHead, { width: TABLE_WIDTH }]}>
              <ThCell width={COL.sr} label="Sr." />
              {showProductColumn && (
                <ThCell width={COL.product} label="Product" />
              )}
              <ThCell width={COL.dated} label="Date" />
              <ThCell width={COL.opStock} label="Op. Stock" align="right" />
              <ThCell width={COL.opValue} label="Op. Value" align="right" />
              <ThCell width={COL.purchase} label="Purch. Qty" align="right" />
              <ThCell width={COL.purchaseVal} label="Purch. Value" align="right" />
              <ThCell width={COL.sales} label="Sales Qty" align="right" />
              <ThCell width={COL.salesVal} label="Sales Value" align="right" />
              <ThCell width={COL.closingStock} label="Cl. Stock" align="right" />
              <ThCell width={COL.closingVal} label="Cl. Value" align="right" />
            </View>

            {generateData()}

            {productList.length > 0 && (
              <View style={[saleReportStyles.totalsRow, { width: TABLE_WIDTH }]}>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: labelWidth, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.thValue}>Total</Text>
                </View>
                <TdCell
                  width={COL.purchase}
                  value={fmtQty(reportData.TotalPurchaseQty)}
                  align="right"
                />
                <TdCell
                  width={COL.purchaseVal}
                  value={fmtAmt(reportData.TotalPurchaseValue)}
                  align="right"
                />
                <TdCell
                  width={COL.sales}
                  value={fmtQty(reportData.TotalSaleQty)}
                  align="right"
                />
                <TdCell
                  width={COL.salesVal}
                  value={fmtAmt(reportData.TotalSaleValue)}
                  align="right"
                />
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.closingStock + COL.closingVal },
                  ]}
                />
              </View>
            )}
          </View>

          <View
            style={{ marginBottom: 20, marginTop: 0, top: 6, bottom: 6 }}
            fixed
          >
            <Text
              style={{ fontSize: "7pt", color: "#000" }}
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} / ${totalPages}`
              }
            />
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%" fileName="ProductWiseSummary.pdf">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
};

export default PrintProductWiseSummary;
